import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Search, ChevronLeft, ChevronRight, Filter, Clock, User, Activity, RefreshCw } from "lucide-react";

const ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  unarchive: "Unarchived",
  assign: "Assigned",
  unassign: "Unassigned",
  clear_all: "Cleared All",
  import: "Imported",
  import_archive: "Imported Archive",
  seed: "Seeded",
  sync_api: "API Sync",
  sync_excel: "Excel Sync",
  disconnect: "Disconnected",
  login: "Logged In",
  logout: "Logged Out",
};

const ACTION_COLORS = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-red-50 text-red-700 border-red-200",
  unarchive: "bg-amber-50 text-amber-700 border-amber-200",
  assign: "bg-violet-50 text-violet-700 border-violet-200",
  unassign: "bg-slate-50 text-slate-600 border-slate-200",
  clear_all: "bg-red-50 text-red-700 border-red-200",
  import: "bg-cyan-50 text-cyan-700 border-cyan-200",
  import_archive: "bg-cyan-50 text-cyan-700 border-cyan-200",
  seed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  sync_api: "bg-teal-50 text-teal-700 border-teal-200",
  sync_excel: "bg-teal-50 text-teal-700 border-teal-200",
  disconnect: "bg-orange-50 text-orange-700 border-orange-200",
  login: "bg-emerald-50 text-emerald-700 border-emerald-200",
  logout: "bg-slate-50 text-slate-600 border-slate-200",
};

const ENTITY_LABELS = {
  project: "Project",
  contract: "Contract",
  account: "Account",
  reconcile: "Reconcile",
  no_bid: "No Bid",
  account_manager: "Account Manager",
  procore: "Procore",
  analytics: "Analytics",
  auth: "Authentication",
};

function ActionBadge({ action }) {
  const label = ACTION_LABELS[action] || action;
  const color = ACTION_COLORS[action] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${color}`} data-testid={`action-badge-${action}`}>
      {label}
    </span>
  );
}

function EntityBadge({ type }) {
  const label = ENTITY_LABELS[type] || type;
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200" data-testid={`entity-badge-${type}`}>
      {label}
    </span>
  );
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [filters, setFilters] = useState({ entity_types: [], actions: [], users: [] });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (entityFilter) params.entity_type = entityFilter;
      if (actionFilter) params.action = actionFilter;
      if (userFilter) params.user_email = userFilter;
      const data = await api.getAuditLogs(params);
      setLogs(data.logs);
      setTotalPages(data.pages);
      setTotal(data.total);
      setFilters(data.filters);
    } catch (e) {
      console.error("Failed to fetch audit logs:", e);
    } finally {
      setLoading(false);
    }
  }, [page, search, entityFilter, actionFilter, userFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, entityFilter, actionFilter, userFilter]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="audit-log-page">
      {/* Header */}
      <div className="px-4 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-slate-600" />
            <h1 className="text-lg font-semibold text-slate-900" data-testid="audit-log-title">Audit Log</h1>
            <span className="text-xs text-slate-400 ml-2">{total} entries (90-day retention)</span>
          </div>
          <button
            onClick={fetchLogs}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
            data-testid="refresh-audit-logs"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="audit-search-input"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="audit-entity-filter"
            >
              <option value="">All Entities</option>
              {filters.entity_types.map((t) => (
                <option key={t} value={t}>{ENTITY_LABELS[t] || t}</option>
              ))}
            </select>

            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="audit-action-filter"
            >
              <option value="">All Actions</option>
              {filters.actions.map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
              ))}
            </select>

            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="audit-user-filter"
            >
              <option value="">All Users</option>
              {filters.users.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          {(entityFilter || actionFilter || userFilter || search) && (
            <button
              onClick={() => { setEntityFilter(""); setActionFilter(""); setUserFilter(""); setSearch(""); }}
              className="text-xs text-blue-600 hover:text-blue-700 underline"
              data-testid="clear-all-filters"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48" data-testid="audit-loading">
            <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400" data-testid="audit-empty">
            <Clock className="h-8 w-8 mb-2" />
            <p className="text-sm">No audit log entries found</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="audit-log-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-40">Time</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-40">User</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-28">Action</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-32">Entity</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Details</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-28">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((log, i) => (
                <tr key={log.id || i} className="hover:bg-slate-50/50 transition-colors" data-testid={`audit-row-${i}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Clock className="h-3 w-3 flex-shrink-0" />
                      <span title={log.timestamp}>{formatTimestamp(log.timestamp)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <User className="h-3 w-3 text-slate-400 flex-shrink-0" />
                      <span className="text-xs text-slate-700 truncate max-w-[120px]" title={log.user_email}>
                        {log.user_name || log.user_email || "System"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><ActionBadge action={log.action} /></td>
                  <td className="px-4 py-2.5"><EntityBadge type={log.entity_type} /></td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs text-slate-600 font-mono truncate block max-w-md" title={log.details}>
                      {log.entity_name ? `${log.entity_name} — ` : ""}{log.details}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs text-slate-400 font-mono">{log.ip_address}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between" data-testid="audit-pagination">
          <span className="text-xs text-slate-500">
            Page {page} of {totalPages} ({total} total)
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="audit-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="audit-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
